import { createContext, ReactNode, useCallback, useMemo } from 'react';
import {
  JSONObject,
  GridInputValue,
  LayoutCanvas,
  LayoutInlineForm,
  LayoutObject,
} from '../../types/types';
import { HandlerRequestContext } from '../../api/types/types';
import {
  LayoutHandlersContextType,
  LayoutHandlerScope,
} from '../../types/context';
import { useLayoutEditorContext } from '../../hooks/context/useLayoutEditorContext';
import { useRequestDataContext } from '../../hooks/context/useRequestDataContext';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import {
  FieldHandlerEngine,
  useFieldHandlerEngine,
} from '../../hooks/useFieldHandlerEngine';
import { getHandlerApiMethods } from '../../utils/handlers/getHandlerApiMethods';
import { coerceLayoutFieldValue } from '../../utils/layouts/coerceLayoutFieldValue';

export const defaultValues: LayoutHandlersContextType = {
  isProvided: false,
  getFieldCallbacks: () => ({}),
  fireFieldEvent: () => {},
};

export const LayoutHandlersContext =
  createContext<LayoutHandlersContextType>(defaultValues);

// Layout editor adapter over the shared field handler engine (the same one
// dataset forms use): fires the handlerTriggers of the definition inline forms
// and merges the response into the layout state. Each scope has its own engine
// instance — handler methods come from that scope's `apiMethods`
// (objectForm/canvasForm/matrixForm) and the response patches its own state.
const LayoutHandlersProvider = ({ children }: { children: ReactNode }) => {
  const {
    definition,
    canvas,
    objects,
    selectedIndex,
    matrixFormData,
    updateObject,
    patchCanvas,
    patchMatrixFormData,
  } = useLayoutEditorContext();
  const requestData = useRequestDataContext();
  const handleApiError = useApiErrorHandling();
  // layoutEditor-scoped responseActions of the handler apiMethods.
  const { getResponseActionHandler } = useResponseActionsContext();

  // Base scope for the {{ }} templates: { datasetId, sessionId, id } + the
  // raw form values + the fire-time capture the apply step reads back.
  // Deliberately NOT the sanitized save body — the saved rules (onPOST/onPUT,
  // omitIfEmpty) would strip helper fields (e.g. a select that only feeds an
  // output handler) that handlers must be able to read.
  const buildScopeContext = (
    values: Record<string, unknown>,
    formPatch?: Record<string, unknown>,
    focusedItem?: string,
    targetIndex?: number
  ): HandlerRequestContext | undefined => {
    if (!requestData) {
      return undefined;
    }

    // formPatch carries the just-changed field value so the handler sees it
    // even before React commits the setState.
    const mergedValues = { ...values, ...(formPatch ?? {}) };
    // FE-only object identity — never exposed to expressions.
    delete mergedValues.feKey;

    // Cast: the layout request data + form values are JSON-shaped, TS just
    // can't prove it.
    return {
      ...(requestData as unknown as JSONObject),
      form: mergedValues,
      ...(focusedItem !== undefined && { focusedItem }),
      ...(targetIndex !== undefined && { targetIndex }),
    } as HandlerRequestContext;
  };

  const objectEngine = useFieldHandlerEngine({
    getApiMethod: (method) =>
      getHandlerApiMethods(definition?.objectForm?.apiMethods)[method],
    getItems: () => definition?.objectForm?.items ?? [],
    buildContext: (formPatch, focusedItem) => {
      if (selectedIndex === null) {
        return undefined;
      }

      return buildScopeContext(
        objects[selectedIndex] ?? {},
        formPatch,
        focusedItem,
        selectedIndex
      );
    },
    // The response patches the object captured at fire time — the selection
    // may have moved to another object while the request was in flight.
    applyFieldUpdates: (updates, context) => {
      const targetIndex = context.targetIndex;

      if (typeof targetIndex !== 'number') {
        return;
      }

      updateObject(targetIndex, updates as Partial<LayoutObject>);
    },
    getResponseActionHandler,
    handleApiError,
  });

  const canvasEngine = useFieldHandlerEngine({
    getApiMethod: (method) =>
      getHandlerApiMethods(definition?.canvasForm?.apiMethods)[method],
    getItems: () => definition?.canvasForm?.items ?? [],
    buildContext: (formPatch, focusedItem) =>
      buildScopeContext(
        canvas as Record<string, unknown>,
        formPatch,
        focusedItem
      ),
    applyFieldUpdates: (updates) => {
      patchCanvas(updates as Partial<LayoutCanvas>);
    },
    getResponseActionHandler,
    handleApiError,
  });

  const matrixEngine = useFieldHandlerEngine({
    getApiMethod: (method) =>
      getHandlerApiMethods(definition?.matrixForm?.apiMethods)[method],
    getItems: () => definition?.matrixForm?.items ?? [],
    buildContext: (formPatch, focusedItem) =>
      buildScopeContext(matrixFormData ?? {}, formPatch, focusedItem),
    applyFieldUpdates: (updates) => {
      Object.entries(updates).forEach(([name, value]) => {
        patchMatrixFormData(name, value as GridInputValue);
      });
    },
    getResponseActionHandler,
    handleApiError,
  });

  const engines: Record<LayoutHandlerScope, FieldHandlerEngine> = useMemo(
    () => ({
      object: objectEngine,
      canvas: canvasEngine,
      matrix: matrixEngine,
    }),
    [objectEngine, canvasEngine, matrixEngine]
  );

  const getFieldCallbacks = useCallback<
    LayoutHandlersContextType['getFieldCallbacks']
  >(
    (scope, name, control, triggers) =>
      engines[scope].getFieldCallbacks(name, control, triggers),
    [engines]
  );

  const scopeForms: Record<
    LayoutHandlerScope,
    LayoutInlineForm | undefined
  > = useMemo(
    () => ({
      object: definition?.objectForm,
      canvas: definition?.canvasForm,
      matrix: definition?.matrixForm,
    }),
    [definition]
  );

  // Programmatic fire — the fullscreen reference confirm has no input callback
  // to go through, so it fires the field's handlers here (change → touch, the
  // same order an autocomplete selection produces).
  const fireFieldEvent = useCallback<
    LayoutHandlersContextType['fireFieldEvent']
  >(
    (scope, name, action, value) => {
      const item = scopeForms[scope]?.items.find(
        (formItem) => formItem.name === name
      );

      if (!item) {
        return;
      }

      const formPatch =
        value !== undefined
          ? {
              [name]: coerceLayoutFieldValue(
                item.type,
                value as GridInputValue
              ),
            }
          : undefined;

      engines[scope].fireEvent(name, item.control, action, formPatch);
    },
    [engines, scopeForms]
  );

  const value = useMemo<LayoutHandlersContextType>(
    () => ({ isProvided: true, getFieldCallbacks, fireFieldEvent }),
    [getFieldCallbacks, fireFieldEvent]
  );

  return (
    <LayoutHandlersContext.Provider value={value}>
      {children}
    </LayoutHandlersContext.Provider>
  );
};

export default LayoutHandlersProvider;
